import {Component, inject, OnDestroy, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ExecutionStatus, IntegrationsApiService} from '@core/services/api/integrations-api.service';
import {AlertService} from '@core/services/alert.service';
import {AppTableComponent} from '@shared/components/tables/app-table/app-table.component';
import {AppTableBodyTemplateDirective, AppTableHeaderTemplateDirective} from '@shared/components/tables/app-table/directives/app-table.directives';
import {AppTableHeaderColumnComponent} from '@shared/components/tables/app-table-header-column/app-table-header-column.component';
import {AppPaginationComponent} from '@shared/components/tables/app-pagination/app-pagination.component';
import {AppPaginationBaseAbstract} from '@shared/commons/pagination-base-abstract.utils';
import {DurationPipe} from '@shared/pipes/duration.pipe';
import {AppSelectComponent} from '@shared/components/forms/app-select/app-select.component';
import {AppLoadingOverlayComponent} from '@shared/components/commons/app-loading-overlay/app-loading-overlay.component';
import {AppEmptyAlertMessageComponent} from '@shared/components/commons/app-empty-alert-message/app-empty-alert-message.component';
import {interval, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PgfnSyncOptionsModalComponent} from './components/pgfn-sync-options-modal.component';

@Component({
    selector: 'app-executions-history',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AppTableComponent,
        AppTableHeaderTemplateDirective,
        AppTableBodyTemplateDirective,
        AppTableHeaderColumnComponent,
        AppPaginationComponent,
        DurationPipe,
        AppSelectComponent,
        AppLoadingOverlayComponent,
        AppEmptyAlertMessageComponent
    ],
    templateUrl: './executions-history.component.html',
    styleUrl: './executions-history.component.scss'
})
export class ExecutionsHistoryComponent extends AppPaginationBaseAbstract implements OnDestroy {
    private integrationsApiService = inject(IntegrationsApiService);
    private alertService = inject(AlertService);
    private modalService = inject(NgbModal);

    protected items: any[] = [];
    protected expandedRowId = signal<number | null>(null);

    // Execution status polling
    protected isSyncRunning = signal<boolean>(false);
    protected isSyncing = signal<boolean>(false);
    private statusPollingSubscription?: Subscription;
    private readonly POLLING_INTERVAL = 5000; // 5 seconds

    // Filter options
    protected years: string[] = [];
    protected states = [
        {name: 'Acre', value: 'ACRE'},
        {name: 'Alagoas', value: 'ALAGOAS'},
        {name: 'Amapá', value: 'AMAPA'},
        {name: 'Amazonas', value: 'AMAZONAS'},
        {name: 'Bahia', value: 'BAHIA'},
        {name: 'Ceará', value: 'CEARA'},
        {name: 'Distrito Federal', value: 'DISTRITO FEDERAL'},
        {name: 'Espírito Santo', value: 'ESPIRITO SANTO'},
        {name: 'Goiás', value: 'GOIAS'},
        {name: 'Maranhão', value: 'MARANHAO'},
        {name: 'Mato Grosso', value: 'MATO GROSSO'},
        {name: 'Mato Grosso do Sul', value: 'MATO GROSSO DO SUL'},
        {name: 'Minas Gerais', value: 'MINAS GERAIS'},
        {name: 'Pará', value: 'PARA'},
        {name: 'Paraíba', value: 'PARAIBA'},
        {name: 'Paraná', value: 'PARANA'},
        {name: 'Pernambuco', value: 'PERNAMBUCO'},
        {name: 'Piauí', value: 'PIAUI'},
        {name: 'Rio de Janeiro', value: 'RIO DE JANEIRO'},
        {name: 'Rio Grande do Norte', value: 'RIO GRANDE DO NORTE'},
        {name: 'Rio Grande do Sul', value: 'RIO GRANDE DO SUL'},
        {name: 'Rondônia', value: 'RONDONIA'},
        {name: 'Roraima', value: 'RORAIMA'},
        {name: 'Santa Catarina', value: 'SANTA CATARINA'},
        {name: 'São Paulo', value: 'SAO PAULO'},
        {name: 'Sergipe', value: 'SERGIPE'},
        {name: 'Tocantins', value: 'TOCANTINS'}
    ];

    protected statusOptions = [
        {name: 'Processando', value: ExecutionStatus.PROCESSING},
        {name: 'Sucesso', value: ExecutionStatus.SUCCESS},
        {name: 'Falha', value: ExecutionStatus.FAILED},
        {name: 'Indisponível', value: ExecutionStatus.UNAVAILABLE}
    ];

    constructor() {
        super();
    }

    override onInit(): void {
        this.generateYearOptions();
        this.addCustomFilters({
            year: null,
            state: null,
            status: null,
            executionType: 'PGFN_SYNC'
        });

        // Start polling for execution status
        this.startStatusPolling();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.stopStatusPolling();
    }

    private startStatusPolling(): void {
        // Check immediately
        this.checkExecutionStatus();

        // Then poll every 5 seconds
        this.statusPollingSubscription = interval(this.POLLING_INTERVAL)
            .pipe(
                switchMap(() => this.integrationsApiService.getPgfnExecutionStatus())
            )
            .subscribe({
                next: (response: any) => {
                    const wasRunning = this.isSyncRunning();
                    this.isSyncRunning.set(response?.data?.isRunning || false);

                    // If sync just finished, reload the list
                    if (wasRunning && !response?.data?.isRunning) {
                        this.alertService.success('Sincronização Concluída', 'A sincronização PGFN foi finalizada.');
                        this.search();
                    }
                },
                error: (error) => {
                    console.error('Error checking execution status:', error);
                }
            });
    }

    private stopStatusPolling(): void {
        if (this.statusPollingSubscription) {
            this.statusPollingSubscription.unsubscribe();
        }
    }

    private checkExecutionStatus(): void {
        this.integrationsApiService.getPgfnExecutionStatus()
            .subscribe({
                next: (response: any) => {
                    this.isSyncRunning.set(response?.data?.isRunning || false);
                },
                error: (error) => {
                    console.error('Error checking execution status:', error);
                }
            });
    }

    private generateYearOptions(): void {
        const currentYear = new Date().getFullYear();
        this.years = [];
        for (let year = currentYear; year >= 2020; year--) {
            this.years.push(year.toString());
        }
    }

    findAll(params: any): void {
        this.integrationsApiService.getPgfnExecutionHistory(params)
            .subscribe({
                next: (response: any) => {
                    const data = response.data;
                    this.items = data.result || [];
                    this.pagination.itemsAmount = data.totalResults || 0;
                    this.pagination.loading = false;
                },
                error: () => {
                    this.alertService.operationFailure();
                    this.pagination.loading = false;
                }
            });
    }

    protected toggleExpandRow(id: number): void {
        if (this.expandedRowId() === id) {
            this.expandedRowId.set(null);
        } else {
            this.expandedRowId.set(id);
        }
    }

    protected startSync(): void {
        const modalRef = this.modalService.open(PgfnSyncOptionsModalComponent, {
            centered: true,
            backdrop: 'static'
        });

        modalRef.result.then(
            (result: any) => {
                // User confirmed, start sync with selected options
                this.executeSyncWithOptions(result);
            },
            () => {
                // User cancelled, do nothing
            }
        );
    }

    private executeSyncWithOptions(options: { mode: string; lastYears?: number; year?: number }): void {
        this.isSyncing.set(true);

        const params = options.mode === 'lastYears'
            ? { lastYears: options.lastYears }
            : { year: options.year };

        this.integrationsApiService.startPgfnSync(params)
            .subscribe({
                next: () => {
                    const message = options.mode === 'lastYears'
                        ? `Sincronização iniciada para os últimos ${options.lastYears} ano(s).`
                        : `Sincronização iniciada para o ano ${options.year}.`;

                    this.alertService.success(
                        'Sincronização Iniciada',
                        message + ' Você será notificado quando concluir.'
                    );
                    this.isSyncing.set(false);
                    this.checkExecutionStatus();
                    this.search(); // Reload to show new executions
                },
                error: (error) => {
                    console.error('Error starting sync:', error);
                    this.alertService.error(
                        'Erro ao Iniciar Sincronização',
                        error.error?.message || 'Não foi possível iniciar a sincronização.'
                    );
                    this.isSyncing.set(false);
                }
            });
    }

    protected getStatusBadgeClass(status: ExecutionStatus): string {
        const classes: Record<ExecutionStatus, string> = {
            [ExecutionStatus.SUCCESS]: 'badge bg-success-subtle text-success',
            [ExecutionStatus.FAILED]: 'badge bg-danger-subtle text-danger',
            [ExecutionStatus.UNAVAILABLE]: 'badge bg-warning-subtle text-warning',
            [ExecutionStatus.PROCESSING]: 'badge bg-info-subtle text-info'
        };
        return classes[status] || 'badge bg-secondary-subtle text-secondary';
    }

    protected getStatusLabel(status: ExecutionStatus): string {
        const labels: Record<ExecutionStatus, string> = {
            [ExecutionStatus.SUCCESS]: 'Sucesso',
            [ExecutionStatus.FAILED]: 'Falha',
            [ExecutionStatus.UNAVAILABLE]: 'Indisponível',
            [ExecutionStatus.PROCESSING]: 'Processando'
        };
        return labels[status] || status;
    }

    protected getStateLabel(state: string): string {
        const stateObj = this.states.find(s => s.value === state);
        return stateObj?.name || state;
    }
}
